import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyViewModBusTelemetryHistoryByPartitionKeyQuery,
  useLazyViewModBusTelemetryLiveByPartitionKeyQuery,
} from "../../../redux/api/mod-bus/modBusAPI";
import {
  selectModBusDashboardFilters,
  selectModBusDevice,
  setModBusIsDefined,
} from "../../../redux/features/mob-bus/mob-bus-slice";
import IGetDeviceTelemetryHistoryResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/get-device-telemetry-history-response-dto";
import IResolvedRegisterDTO from "../../../shared/oversight-core/dtos/response-dtos/resolved-register-dto";
import { ITelemetrySnapshotDTO } from "../../../shared/oversight-core/dtos/response-dtos/telemetry-snapshot-dto";
import { EDateTypes } from "../../../shared/oversight-core/enums/date-types";
import ActiveInactiveIndicator from "../../../shared/oversight-core/ui-elements/active-inactive-indicator/active-inactive-indicator";
import AppDropDown from "../../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppSelect, {
  Option,
} from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import AreaChart from "../../../shared/oversight-core/ui-elements/area-chart/area-chart";
import ButtonWithBadge from "../../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import DeleteConfigurationRegistersModal from "./components/delete-configuration-registers-modal";
import FilterMonitorSidePanel from "./components/filter-monitor-side-panel";
import LiveTelemetryRegisterCard from "./components/live-telemetry-register-card";
import TelemetryDataConfigureModal from "./components/telemetry-data-configure-modal";

const getSnapshotChartLabel = ({
  snapshot,
  previousSnapshot,
  selectedDateType,
}: {
  snapshot?: ITelemetrySnapshotDTO;
  previousSnapshot?: ITelemetrySnapshotDTO;
  selectedDateType: EDateTypes;
}) => {
  if (!snapshot) {
    return "";
  }

  if (selectedDateType === EDateTypes.DAILY) {
    return moment.utc(snapshot.enqueuedTime).local().format("HH:mm");
  }

  const currentDateStr = moment
    .utc(snapshot.enqueuedTime)
    .local()
    .format("DD/MM/YYYY");

  if (previousSnapshot) {
    const previousDateStr = moment
      .utc(previousSnapshot.enqueuedTime)
      .local()
      .format("DD/MM/YYYY");
    if (currentDateStr === previousDateStr) {
      return "";
    }
  }

  return currentDateStr;
};

const getRegisterChartValue = (register?: IResolvedRegisterDTO) => {
  if (!register) {
    return 0;
  }

  const value = register.defined
    ? Number(register.displayValue)
    : Number(register.rawValue);
  return Number.isFinite(value) ? value : 0;
};

const Monitor = () => {
  const modBus = useSelector(selectModBusDevice);
  const modBusFilterFilterStore = useSelector(selectModBusDashboardFilters);
  const dispatch = useDispatch();

  const [telemetrySnapshots, setTelemetrySnapshots] = useState<
    ITelemetrySnapshotDTO[]
  >([]);
  const [selectedRegisterKey, setSelectedRegisterKey] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [showTelemetryDataConfigure, setShowTelemetryDataConfigure] =
    useState(false);
  const [
    showDeleteConfigurationRegisters,
    setShowDeleteConfigurationRegisters,
  ] = useState(false);
  const [liveRegisters, setLiveRegisters] = useState<IResolvedRegisterDTO[]>(
    [],
  );
  const [lastUpdateDate, setLastUpdateDate] = useState("");
  const [isUpdateCurrentState, setIsUpdateCurrentState] = useState(false);
  const [isFirstLiveTelemetryFetch, setIsFirstLiveTelemetryFetch] =
    useState(true);

  const liveTelemetryRequestIdRef = useRef(0);

  const [
    triggerViewModBusTelemetryHistory,
    { isFetching: isFetchingViewModBusTelemetryHistory },
  ] = useLazyViewModBusTelemetryHistoryByPartitionKeyQuery();
  const [
    triggerViewModBusTelemetryLive,
    { isFetching: isFetchingViewModBusTelemetryLive },
  ] = useLazyViewModBusTelemetryLiveByPartitionKeyQuery();

  const registerOptions = useMemo<Option[]>(() => {
    const registerMap = new Map<string, Option>();

    telemetrySnapshots.forEach((snapshot) => {
      snapshot.registers.forEach((register) => {
        if (!registerMap.has(register.registerKey)) {
          registerMap.set(register.registerKey, {
            value: register.registerKey,
            label: register.label,
            data: register,
          });
        }
      });
    });

    return Array.from(registerMap.values());
  }, [telemetrySnapshots]);

  const selectedRegisterOption = useMemo(
    () =>
      registerOptions.find((option) => option.value === selectedRegisterKey),
    [registerOptions, selectedRegisterKey],
  );

  const selectedRegisterSnapshots = useMemo(
    () =>
      telemetrySnapshots
        .map((snapshot) => ({
          snapshot,
          register: snapshot.registers.find(
            (register) => register.registerKey === selectedRegisterKey,
          ),
        }))
        .filter(
          (
            item,
          ): item is {
            snapshot: ITelemetrySnapshotDTO;
            register: IResolvedRegisterDTO;
          } => Boolean(item.register),
        ),
    [selectedRegisterKey, telemetrySnapshots],
  );

  const chartValue = useMemo(
    () =>
      selectedRegisterSnapshots.map(({ register }) =>
        getRegisterChartValue(register),
      ),
    [selectedRegisterSnapshots],
  );

  const chartLabels = useMemo(() => {
    return selectedRegisterSnapshots.map(({ snapshot }, index) => {
      const previousSnapshot =
        index > 0 ? selectedRegisterSnapshots[index - 1].snapshot : undefined;
      return getSnapshotChartLabel({
        snapshot,
        previousSnapshot,
        selectedDateType: modBusFilterFilterStore.selectedDateType,
      });
    });
  }, [selectedRegisterSnapshots, modBusFilterFilterStore.selectedDateType]);

  const tooltipLabels = useMemo(() => {
    return selectedRegisterSnapshots.map(({ snapshot }) =>
      moment.utc(snapshot.enqueuedTime).local().format("DD/MM/YYYY HH:mm"),
    );
  }, [selectedRegisterSnapshots]);

  const selectedRegister = selectedRegisterOption?.data as
    | IResolvedRegisterDTO
    | undefined;
  const selectedRegisterUnit = selectedRegister?.unit || "";
  const isLiveTelemetryInitialFetching =
    isFetchingViewModBusTelemetryLive && isFirstLiveTelemetryFetch;

  useEffect(() => {
    if (modBus?.partitionKey) {
      const selectedDate = moment(modBusFilterFilterStore.selectedDate).format(
        "YYYY-MM-DD",
      );
      const selectedUtcDate = moment.utc(selectedDate);
      let fromDate = selectedUtcDate.clone().startOf("month");
      let toDate = selectedUtcDate.clone().endOf("month");

      if (modBusFilterFilterStore.selectedDateType === EDateTypes.DAILY) {
        fromDate = selectedUtcDate.clone().startOf("day");
        toDate = selectedUtcDate.clone().endOf("day");
      } else if (
        modBusFilterFilterStore.selectedDateType === EDateTypes.WEEKLY
      ) {
        fromDate = selectedUtcDate.clone().subtract(7, "days").startOf("day");
        toDate = selectedUtcDate.clone().endOf("day");
      } else if (
        modBusFilterFilterStore.selectedDateType === EDateTypes.MONTHLY
      ) {
        fromDate = selectedUtcDate.clone().startOf("month");
        toDate = selectedUtcDate.clone().endOf("month");
      }

      triggerViewModBusTelemetryHistory({
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
        partitionKey: modBus.partitionKey,
        graphType: modBusFilterFilterStore.selectedDateType,
      })
        .unwrap()
        .then((res: IGetDeviceTelemetryHistoryResponseDTO) => {
          const snapshots = res.snapshots || [];
          setTelemetrySnapshots(snapshots);
          setSelectedRegisterKey((currentRegisterKey) => {
            const hasSelectedRegister = snapshots.some((snapshot) =>
              snapshot.registers.some(
                (register) => register.registerKey === currentRegisterKey,
              ),
            );

            if (hasSelectedRegister) {
              return currentRegisterKey;
            }

            return snapshots[0]?.registers[0]?.registerKey || "";
          });
        })
        .catch(() => {
          setTelemetrySnapshots([]);
          setSelectedRegisterKey("");
        });
    } else {
      setTelemetrySnapshots([]);
      setSelectedRegisterKey("");
    }
  }, [
    triggerViewModBusTelemetryHistory,
    modBus.partitionKey,
    modBusFilterFilterStore,
  ]);

  useEffect(() => {
    if (modBus.isDefined && modBus.partitionKey) {
      const requestId = liveTelemetryRequestIdRef.current + 1;
      liveTelemetryRequestIdRef.current = requestId;
      setIsFirstLiveTelemetryFetch(true);
      triggerViewModBusTelemetryLive({ partitionKey: modBus.partitionKey })
        .unwrap()
        .then((res) => {
          setLiveRegisters(res.registers || []);
          setLastUpdateDate(res.enqueuedTime || "");
        })
        .catch(() => {
          setLiveRegisters([]);
          setLastUpdateDate("");
        })
        .finally(() => {
          if (liveTelemetryRequestIdRef.current === requestId) {
            setIsFirstLiveTelemetryFetch(false);
          }
        });
    } else {
      liveTelemetryRequestIdRef.current += 1;
      setLiveRegisters([]);
      setLastUpdateDate("");
      setIsFirstLiveTelemetryFetch(false);
    }
  }, [
    modBus.isDefined,
    modBus.partitionKey,
    triggerViewModBusTelemetryLive,
    isUpdateCurrentState,
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (modBus.isDefined && modBus.partitionKey) {
        triggerViewModBusTelemetryLive({ partitionKey: modBus.partitionKey })
          .unwrap()
          .then((res) => {
            setLiveRegisters(res.registers || []);
            setLastUpdateDate(res.enqueuedTime || "");
          })
          .catch(() => {
            setLiveRegisters([]);
            setLastUpdateDate("");
          });
      } else {
        setLiveRegisters([]);
        setLastUpdateDate("");
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [
    modBus.isDefined,
    modBus.partitionKey,
    triggerViewModBusTelemetryLive,
    isUpdateCurrentState,
  ]);

  return (
    <>
      <Row className="mt-4">
        <Col>
          <div className="bg-white p-3 rounded-3">
            <Row className="justify-content-end">
              <Col className="col-12 col-md-4 col-xl-3">
                <AppSelect
                  options={registerOptions}
                  selectedValue={selectedRegisterOption}
                  placeholder="Select register key"
                  disabled={
                    isFetchingViewModBusTelemetryHistory ||
                    registerOptions.length === 0
                  }
                  onChangeOption={(option) => {
                    setSelectedRegisterKey(option.value);
                  }}
                  menuHeight="250px"
                />
              </Col>
              <Col className="col-auto">
                <ButtonWithBadge
                  text="Filter"
                  icon="filter_alt"
                  onClick={() => setShowFilter(true)}
                  badgeValue={0}
                />
              </Col>
            </Row>
            {!isFetchingViewModBusTelemetryHistory ? (
              <AreaChart
                data1={chartValue}
                labels={chartLabels}
                tooltipTitleCallback={(context) => {
                  return tooltipLabels[context[0].dataIndex];
                }}
                borderColor1="#ECD58A"
                backgroundColor1="#F5FFCD4D"
                yAxesUnit={selectedRegisterUnit}
                label1="Telemetry Data"
                isChartDataAvailable={chartValue.some((dd) => dd !== 0)}
              />
            ) : (
              <SpinnerModal
                show={isFetchingViewModBusTelemetryHistory}
                withOverlay={false}
              />
            )}
          </div>
        </Col>
      </Row>
      {modBus.isDefined ? (
        <div className="bg-white p-3 rounded-3 mt-4">
          <Row className="align-items-center">
            <Col>
              <Row className="row-cols-auto align-items-center">
                <Col className="font-size-14 text-dark font-weight-500 pe-0">
                  Live Telemetry Data
                </Col>
                <Col>
                  <ActiveInactiveIndicator isActive={true} isLive={true} />
                </Col>
              </Row>
            </Col>
            <Col className="col-auto">
              <Row className="row-cols-auto align-items-center g-2">
                <Col>
                  <AppDropDown
                    items={[
                      {
                        text: "Edit",
                        onClick: () => setShowTelemetryDataConfigure(true),
                      },
                      {
                        text: "Delete",
                        onClick: () =>
                          setShowDeleteConfigurationRegisters(true),
                      },
                    ]}
                  />
                </Col>
                {lastUpdateDate && (
                  <Col>
                    <Row className="row-cols-auto align-items-center text-light font-size-14 font-weight-400">
                      <Col className="pe-0">Last Updated:</Col>
                      <Col>
                        {moment(lastUpdateDate).format("YYYY/MM/DD HH:mm: A")}
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row className="mt-3 g-3">
            {isLiveTelemetryInitialFetching ? (
              <Col>
                <SpinnerModal show={true} withOverlay={false} />
              </Col>
            ) : liveRegisters.length > 0 ? (
              liveRegisters.map((register) => (
                <Col
                  key={register.registerKey}
                  className="col-12 col-md-6 col-xl-3"
                >
                  <LiveTelemetryRegisterCard register={register} />
                </Col>
              ))
            ) : (
              <Col className="font-size-14 text-light text-center py-4">
                No live telemetry data available.
              </Col>
            )}
          </Row>
        </div>
      ) : (
        <div className="container-dash mt-4 mb-4">
          <Row className="font-size-14 justify-content-center">
            <Col className="col-auto text-light">
              There are no data configure.
            </Col>
          </Row>
          <Row>
            <Col
              className="font-size-14 text-decoration-underline text-center mt-1 text-primary cursor-pointer"
              onClick={() => setShowTelemetryDataConfigure(true)}
            >
              Configure Register
            </Col>
          </Row>
        </div>
      )}
      <FilterMonitorSidePanel
        showFilter={showFilter}
        setShowFilter={(value: boolean) => {
          setShowFilter(value);
        }}
      />
      <TelemetryDataConfigureModal
        show={showTelemetryDataConfigure}
        onClose={() => {
          setShowTelemetryDataConfigure(false);

          if (!modBus.isDefined) {
            dispatch(setModBusIsDefined(false));
          }
        }}
        onCancel={() => {
          setShowTelemetryDataConfigure(false);
          if (!modBus.isDefined) {
            dispatch(setModBusIsDefined(false));
          }
        }}
        closeTelemetryDataConfigureModal={() => {
          setShowTelemetryDataConfigure(false);
          dispatch(setModBusIsDefined(true));
        }}
        partitionKey={modBus.partitionKey}
        deviceName={modBus.deviceName}
        model={modBus.model}
        setIsUpdateCurrentState={setIsUpdateCurrentState}
        isDefined={modBus.isDefined}
      />
      <DeleteConfigurationRegistersModal
        show={showDeleteConfigurationRegisters}
        onClose={() => setShowDeleteConfigurationRegisters(false)}
        onCancel={() => setShowDeleteConfigurationRegisters(false)}
        partitionKey={modBus.partitionKey}
        setIsUpdateCurrentState={setIsUpdateCurrentState}
        onDeleteSuccess={() => {
          dispatch(setModBusIsDefined(false));
        }}
      />
    </>
  );
};

export default Monitor;
